/**
 * The AI Agent's ten subagents, in the order of the design's table (Figma «Agents» 973:9279): each role's name,
 * colour, icon, the tools it is offered and what it may change in the project (`rights.ts`). The AI Agent picks
 * the roles a request needs; nobody chooses them by hand.
 */
import type { Rights } from './rights';
import { isTextOf, t, type TranslationKey } from '../i18n/I18n';

export type RoleId = 'layout' | 'catalog' | 'style' | 'decor' | 'measure' | 'estimate' | 'lighting' | 'render' | 'review' | 'kitchen';

export interface AgentRole {
  id: RoleId;
  nameKey: TranslationKey;
  readonly name: string;
  color: string;
  icon: string;
  halo: string;
  tools: readonly string[];
  rights: Rights;
  /** One line for the AI Agent's list of whom it can hand work to. */
  brief: string;
  /** The role's instructions, appended to the shared ones. */
  prompt: string;
}

/** How long a finished subagent stays on the stage after the answer, ms; the bots it brought leave with it. */
export const AGENT_LINGER_MS = 6000;

const READ = ['get_scene', 'search_catalog', 'search_materials', 'capture_view'];
const PLACE = ['add_item', 'update_item', 'duplicate_item', 'replace_item', 'delete', 'place_along_wall', 'place_beside'];
const NONE: Rights = { structure: false, finish: false, arrange: 'none', stock: 'none' };
const OUT_OF_ROW = 'When a task, or part of it, is outside this, change nothing for it and say in your report that it is not your job.';
const READ_ONLY = 'You only read the project: you change nothing.';

const role = (id: RoleId, nameKey: TranslationKey, color: string, tools: readonly string[], rights: Rights, brief: string, prompt: string): AgentRole => ({
  id, nameKey, get name() { return t(nameKey); }, color, icon: `/hp/ui/agents/${id}.svg`, halo: `/hp/ui/presence/${id}-halo.svg`,
  tools: [...READ, ...tools], rights, brief, prompt: `${prompt} ${OUT_OF_ROW}`,
});

export const ROLES: readonly AgentRole[] = [
  role('layout', '#DemoBotLayoutAgent', '#1D85FD', ['add_room', 'add_wall', 'update_wall', 'add_opening', 'update_opening', 'update_room', 'update_item', 'delete'],
    { ...NONE, structure: true, arrange: 'all' },
    'walls, rooms, doors and windows; moves, turns and resizes items of every kind; no materials, adds no items',
    'You are the Layout Agent: you build and correct the plan — rooms, walls, doors, windows — and move, turn and resize the items already in the project. You choose no materials and add or remove no items.'),
  role('catalog', '#DemoBotCatalogAgent', '#FD4B83', ['add_item', 'duplicate_item', 'replace_item', 'delete', 'place_along_wall', 'place_beside', 'arrange_around', 'furnish_room'],
    { ...NONE, stock: 'all' },
    'adds, replaces and removes items from the catalog, furnishes whole rooms; never moves what is placed, no walls or materials',
    'You are the Catalog Agent: you add, replace and remove items from the catalog and furnish whole rooms. You do not move, turn or resize items already placed, and you touch no walls, openings or materials.'),
  role('style', '#DemoBotStyleAgent', '#16A34A', ['set_material', 'set_room_finish'],
    { ...NONE, finish: true },
    'materials, colours and textures of walls, floors, ceilings and items; no geometry or arrangement',
    'You are the Style Agent: you choose materials, colours and textures for walls, floors, ceilings and items. You change no geometry and move nothing.'),
  role('decor', '#DemoBotDecorAgent', '#5F0D26', PLACE,
    { ...NONE, arrange: ['decor'], stock: ['decor'] },
    'small decor only: cushions, plants, pictures, vases, textiles',
    'You are the Decor Agent: you add, arrange and remove small decor — cushions, plants, pictures, vases, textiles. You leave furniture, walls and materials alone.'),
  role('measure', '#AgentMeasureName', '#2D64B1', [], NONE,
    'reads dimensions and areas and reports them; changes nothing',
    `You are the Measure Agent: you read the project's dimensions and areas and report what you found. ${READ_ONLY} The measured plan comes later: never promise a drawing or a PDF.`),
  role('estimate', '#AgentEstimateName', '#A16207', [], NONE,
    'reads what the project holds for an estimate and reports it; changes nothing',
    `You are the Estimate Agent: you read what the project holds — rooms, areas, items — and report it for an estimate. ${READ_ONLY} The estimate document comes later: never promise a PDF or prices you do not have.`),
  role('lighting', '#AgentLightingName', '#C2410C', PLACE,
    { ...NONE, arrange: ['light'], stock: ['light'] },
    'lights, sockets and switches only',
    'You are the Lighting Agent: you add, arrange and remove lights, sockets and switches. You leave furniture, walls and materials alone.'),
  role('render', '#AgentRenderName', '#0F766E', ['set_view', 'focus', 'render_ai'], NONE,
    'frames the 3D view and makes AI renders; changes nothing in the project',
    `You are the Render Agent: you frame the 3D view and make AI renders in the style asked for. ${READ_ONLY}`),
  role('review', '#AgentReviewName', '#5B3FA8', [], NONE,
    'reviews the project and reports problems; changes nothing',
    `You are the Review Agent: you check the project — circulation, doors kept clear, overlaps, missing pieces — and report what you found. ${READ_ONLY}`),
  role('kitchen', '#AgentKitchenName', '#96462D', PLACE,
    { ...NONE, arrange: ['kitchen'], stock: ['kitchen'] },
    'kitchen modules, appliances and the worktop only',
    'You are the Kitchen Agent: you add, arrange and remove kitchen modules, appliances and worktops. You leave other furniture, walls and materials alone.'),
];

export const ROLE_IDS: readonly RoleId[] = ROLES.map(entry => entry.id);

export const roleById = (id: string): AgentRole | undefined => ROLES.find(entry => entry.id === id);

/** A role by the name a saved chat keeps, in either language. */
export const roleNamed = (name: string): AgentRole | null => ROLES.find(entry => isTextOf(entry.nameKey, name)) ?? null;
